/**
 * MenuBuilderEmbedded — rdzen projektowania domyslnego menu modulu (inline).
 *
 * Wyekstrahowany z TemplateMenuBuilderPage tak, zeby moc osadzac builder
 * bezposrednio w prawym panelu ModuleTemplateEditor (bez nawigacji do osobnej strony).
 *
 * Props:
 *  - templateSlug: kod szablonu (do PATCH /admin/module-templates/:slug/menu)
 *  - entities:     encje szablonu (zrodlo paletki)
 *  - initialMenu:  poczatkowa hierarchia menu (z tpl.defaultMenu)
 *  - readOnly:     gdy true, blokuje zapis (brak przycisku Zapisz, canvas read-only)
 *
 * Reset stanu: gdy initialMenu zmieni sie (porownanie deep przez menuItemsEqual),
 * working copy jest resetowana — stabilne porownanie, nie gola referencja.
 *
 * Wysokość: root wypełnia rodzica o ograniczonej wysokości (`heightClassName`, domyślnie
 * `min-h-0 flex-1` w kolumnie flex — edytor szablonu i samodzielna strona). Kontener
 * paleta+płótno jest overflow-hidden — paleta i płótno przewijają się niezależnie, a
 * wyszukiwarka palety zostaje przyklejona pod jej nagłówkiem.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AdminMenuItem,
  ListConfig,
  MenuItem,
  MenuSourceFunctionalItem,
  MenuSourceKartoteka,
  MenuSourceResponse,
  ModuleTemplateEntityDefinition,
  ModuleTemplateFieldDefinition,
  ModuleTemplateFormConfig,
  ModuleTemplateViewDefinition,
} from '@saas/shared';
import { emptyListConfig, SYSTEM_ITEMS, TENANT_SCREENS } from '@saas/shared';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { Eye, Save } from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import type { KartotekaDefinition } from '@/lib/kartoteki/kartoteka-definition';
import { mapKartotekaFieldsForPreview } from '@/lib/kartoteki/kartoteka-preview-fields';
import { collectUsedPaletteKeys } from '@/lib/menu-dnd';
import {
  findInTree,
  insertIntoContainer,
  menuItemsEqual,
  moveInTree,
  removeFromTree,
  reorderInContainer,
  updateInTree,
} from '@/lib/menu-tree';
import {
  extractTemplateApiErrorCode,
  moduleTemplateDetailQueryKey,
  parseMenuViewRef,
  patchDraftMenu,
  patchTemplateMenu,
  type TemplateMenuViewUpsert,
  useTemplateMenuSource,
} from '@/lib/module-templates/template-layouts';

import { CollapsedColumnRail, ColumnResizer } from './ColumnControls';
import { ForeignKartotekaViewModal } from './ForeignKartotekaViewModal';
import { buildMenuViewUpserts, collectDanglingMenuRefLabels } from './menu-view-upserts';
import { MenuCanvas } from './MenuCanvas';
import type { MenuItemViewLabels } from './MenuItemBlock';
import { MenuItemFormBuilderModal } from './MenuItemFormBuilderModal';
import { MenuItemViewBuilderModal } from './MenuItemViewBuilderModal';
import { MenuPalette } from './MenuPalette';
import { type MenuNodePreviewData, MenuUnifiedPreview } from './MenuUnifiedPreview';
import { foreignLayoutsCode, useForeignMenuViews } from './use-foreign-menu-views';

const SUB_PREFIX = 'admin.menuLayoutBuilder';

export interface MenuBuilderEmbeddedProps {
  templateSlug: string;
  /** Nazwa modułu (szablonu) — podtytuł pod marką w podglądzie menu tenanta. */
  moduleName?: string;
  entities: ModuleTemplateEntityDefinition[];
  initialMenu: MenuItem[];
  readOnly?: boolean;
  /**
   * Id menu ROBOCZEGO modułu — „Zapisz" trafia wtedy do `module_menus` (PATCH .../menus/[id],
   * bez podbicia wersji). Brak = edytujemy WYBRANE menu (`modules.default_menu`, jedzie do
   * klientów po publikacji; PATCH .../menu z bumpem wersji).
   */
  draftMenuId?: string;
  /** Zgłasza rodzicowi niezapisane zmiany (np. blokada „Opublikuj", dopóki menu nie zapisane). */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Klasy wysokości roota. Default `min-h-0 flex-1` — rodzic to kolumna flex o ograniczonej
   * wysokości (edytor szablonu w trybie menu, strona „menu szablonu").
   */
  heightClassName?: string;
}

/** Zakres zapamiętywania zwiniętych sekcji palety — osobny od kreatora menu ITE (ten sam prefiks tłumaczeń). */
const PALETTE_COLLAPSED_SCOPE = 'admin.templateMenuBuilder';

/**
 * Tworzy slug widoku z nazwy (lowercase, znaki spoza [a-z0-9]→podkreślenie, max 33 znaki).
 * Prefiks 'form_' lub 'list_' zapobiega kolizji między typami. Slug musi pasować do wzorca
 * backendu (`[a-z][a-z0-9_]*`) — stąd podkreślenia, nie myślniki.
 */
function slugFromName(name: string, prefix: 'form_' | 'list_' = 'form_'): string {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 33);
  return `${prefix}${base || 'widok'}`;
}

/**
 * Tworzy unikalny slug widoku, dodając sufiks liczbowy gdy slug już istnieje w views.
 */
function uniqueViewSlug(
  name: string,
  existingViews: ModuleTemplateViewDefinition[],
  prefix: 'form_' | 'list_' = 'form_',
): string {
  const base = slugFromName(name, prefix);
  const taken = new Set(existingViews.map((v) => v.slug));
  if (!taken.has(base)) return base;
  for (let i = 2; i <= 99; i++) {
    const candidate = `${base}_${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}_${Date.now()}`;
}

export function MenuBuilderEmbedded({
  templateSlug,
  moduleName,
  entities: entitiesProp,
  initialMenu,
  readOnly = false,
  draftMenuId,
  onDirtyChange,
  heightClassName = 'min-h-0 flex-1',
}: MenuBuilderEmbeddedProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [hierarchy, setHierarchy] = useState<MenuItem[]>(() => initialMenu);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Paleta zwijalna + poszerzalna (ten sam wzorzec co Kreator menu).
  const [paletteOpen, setPaletteOpen] = useState(true);
  const [paletteWidth, setPaletteWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return 256;
    const n = Number(window.localStorage.getItem('templateMenuBuilder.paletteWidth'));
    return Number.isFinite(n) && n > 0 ? Math.min(560, Math.max(200, n)) : 256;
  });
  useEffect(() => {
    window.localStorage.setItem('templateMenuBuilder.paletteWidth', String(paletteWidth));
  }, [paletteWidth]);
  /**
   * Zunifikowany podgląd menu tenanta (sidebar + prawy panel lista/formularz).
   * `previewInitialNodeId`/`previewInitialMode` = selekcja startowa przy wejściu
   * z przycisków przy pozycji; null/'list' przy wejściu z paska buildera.
   */
  const [menuPreviewOpen, setMenuPreviewOpen] = useState(false);
  const [previewInitialNodeId, setPreviewInitialNodeId] = useState<string | null>(null);
  const [previewInitialMode, setPreviewInitialMode] = useState<'form' | 'list'>('list');
  const [viewBuilderNode, setViewBuilderNode] = useState<MenuItem | null>(null);
  /**
   * Slug nazwanego widoku listy otwieranego przez builder. null = domyślny (inline),
   * string = nazwany widok listy (z listRef). undefined = brak otwartego buildera.
   */
  const [viewBuilderListSlug, setViewBuilderListSlug] = useState<string | null | undefined>(
    undefined,
  );
  const [formBuilderNode, setFormBuilderNode] = useState<MenuItem | null>(null);
  /**
   * Layout, od którego builder formularza ma się otworzyć (gdy uruchomiony z modalu
   * przypisania „Przeglądaj/edytuj"). null = domyślny; undefined = z viewRef pozycji.
   */
  const [formBuilderInitialSlug, setFormBuilderInitialSlug] = useState<string | null | undefined>(
    undefined,
  );
  /**
   * Czy aktualnie otwarty builder (formularz lub lista) jest w trybie read-only (Podgląd).
   * true = otworzony przez „Podgląd" (domyślny layout) → modal read-only.
   * false = otworzony przez „Przeglądaj/edytuj" lub create/duplicate → edytowalny.
   */
  const [builderReadOnly, setBuilderReadOnly] = useState(false);

  /**
   * Czy nazwane layouty formularza (entity.views) różnią się od stanu z backendu.
   * Edycja formConfig istniejącego nazwanego widoku nie zmienia hierarchii menu, więc
   * sam `hierarchyDirty` by tego nie złapał — przycisk „Zapisz" zostałby wyłączony.
   * Flaga flipuje true przy create/update widoku, reset po zapisie / refetchu encji.
   */
  const [viewsDirty, setViewsDirty] = useState(false);

  /**
   * Lokalna kopia entities — pozwala na dodawanie nowych widoków `form` bez
   * persystowania do backendu (krok A). Resetowana gdy entitiesProp się zmieni.
   * Widoki tworzone tutaj żyją do czasu zapisu struktury szablonu (PUT structure)
   * albo do zmiany entitiesProp (np. refetch po stronie parenta).
   */
  const [localEntities, setLocalEntities] = useState<ModuleTemplateEntityDefinition[]>(
    () => entitiesProp,
  );

  // Synchronizuj lokalEntities gdy entitiesProp zmieni się z zewnątrz (refetch parenta).
  const prevEntitiesRef = useRef<ModuleTemplateEntityDefinition[]>(entitiesProp);
  useEffect(() => {
    if (entitiesProp !== prevEntitiesRef.current) {
      prevEntitiesRef.current = entitiesProp;
      setLocalEntities(entitiesProp);
      // Świeże encje z backendu (np. po zapisie) = nazwane layouty zsynchronizowane.
      setViewsDirty(false);
    }
  }, [entitiesProp]);

  /**
   * Pola kartotek systemowych dla encji-odwołań (`reference_to_system_entity`) —
   * zasilają zunifikowany podgląd pozycji menu (temat 3: Pracownicy też mają podgląd).
   * Fetch per unikalny slug kartoteki (klucz cache wspólny z `useKartotekaDefinition`);
   * do czasu załadowania resolver zwraca null → prawy panel pokazuje dotychczasową notkę.
   */
  const referencedKartotekaSlugs = useMemo(
    () =>
      Array.from(
        new Set(
          localEntities
            .filter((e) => e.ddlStrategy === 'reference_to_system_entity')
            .map((e) => e.referenceSystemModuleSlug)
            .filter((s): s is string => Boolean(s)),
        ),
      ).sort(),
    [localEntities],
  );
  const kartotekaPreviewFieldsBySlug = useQueries({
    queries: referencedKartotekaSlugs.map((slug) => ({
      queryKey: ['admin', 'kartoteki', slug, 'definition'],
      queryFn: async () =>
        (await api.get<KartotekaDefinition>(`/admin/kartoteki/${slug}/definition`)).data,
      retry: false,
      staleTime: 60_000,
    })),
    combine: (results) => {
      const map = new Map<string, ModuleTemplateFieldDefinition[]>();
      referencedKartotekaSlugs.forEach((slug, i) => {
        const fields = results[i]?.data?.fields;
        if (fields) map.set(slug, mapKartotekaFieldsForPreview(fields));
      });
      return map;
    },
  });

  /**
   * Callback: tworzy nowy widok `form` dla danej encji i zwraca jego slug.
   * Nowy widok seeded jest pełnym domyślnym layoutem (formConfig) — do tego
   * MenuItemFormBuilderModal sam składa initialLayoutConfig z entity.fields.
   * Tutaj zapisujemy tylko metadane (slug/name/isDefault=false) i pusty formConfig.
   * Builder potem wywoła handleFormBuilderSave → zaktualizuje view.formConfig.
   */
  const handleCreateEntityView = (
    entitySlug: string,
    name: string,
    seedFormConfig: ModuleTemplateFormConfig,
  ): string => {
    let createdSlug = '';
    setLocalEntities((prev) =>
      prev.map((e) => {
        if (e.slug !== entitySlug) return e;
        const slug = uniqueViewSlug(name, e.views, 'form_');
        createdSlug = slug;
        const newView: ModuleTemplateViewDefinition = {
          slug,
          name,
          type: 'form',
          isDefault: false,
          sortOrder: e.views.filter((v) => v.type === 'form').length,
          formConfig: seedFormConfig,
        };
        return { ...e, views: [...e.views, newView] };
      }),
    );
    setViewsDirty(true);
    return createdSlug;
  };

  /**
   * Callback: duplikuje istniejący widok `form` dla danej encji i zwraca slug kopii.
   * Nazwa podawana jawnie przez użytkownika (z inputu create z chipem).
   * formConfig jest deep-klonowany przez structuredClone, więc kopia nie dzieli stanu z oryginałem.
   */
  const handleDuplicateEntityView = (
    entitySlug: string,
    sourceSlug: string | null,
    name: string,
  ): string => {
    let createdSlug = '';
    setLocalEntities((prev) =>
      prev.map((e) => {
        if (e.slug !== entitySlug) return e;

        // Rozwiąż źródłowy formConfig: null = domyślny (pusty seed jak przy create),
        // string = nazwany widok z localEntities.
        let sourceFormConfig: ModuleTemplateFormConfig = { elements: [] };
        if (sourceSlug !== null) {
          const sourceView = e.views.find((v) => v.type === 'form' && v.slug === sourceSlug);
          if (sourceView?.formConfig) {
            sourceFormConfig = structuredClone(sourceView.formConfig);
          }
        }

        const slug = uniqueViewSlug(name, e.views, 'form_');
        createdSlug = slug;
        const newView: ModuleTemplateViewDefinition = {
          slug,
          name,
          type: 'form',
          isDefault: false,
          sortOrder: e.views.filter((v) => v.type === 'form').length,
          formConfig: sourceFormConfig,
        };
        return { ...e, views: [...e.views, newView] };
      }),
    );
    setViewsDirty(true);
    return createdSlug;
  };

  /**
   * Callback: tworzy nowy widok `list` dla danej encji i zwraca jego slug.
   * Startuje z pustą konfiguracją listy; builder wywoła handleViewBuilderSave
   * → zaktualizuje view.listConfig.
   */
  const handleCreateEntityListView = (entitySlug: string, name: string): string => {
    let createdSlug = '';
    setLocalEntities((prev) =>
      prev.map((e) => {
        if (e.slug !== entitySlug) return e;
        const slug = uniqueViewSlug(name, e.views, 'list_');
        createdSlug = slug;
        const newView: ModuleTemplateViewDefinition = {
          slug,
          name,
          type: 'list',
          isDefault: false,
          sortOrder: e.views.filter((v) => v.type === 'list').length,
          listConfig: structuredClone(emptyListConfig),
        };
        return { ...e, views: [...e.views, newView] };
      }),
    );
    setViewsDirty(true);
    return createdSlug;
  };

  /**
   * Callback: duplikuje istniejący widok `list` dla danej encji i zwraca slug kopii.
   * Nazwa podawana jawnie przez użytkownika (z inputu create z chipem).
   * listConfig jest deep-klonowany przez structuredClone, więc kopia nie dzieli stanu z oryginałem.
   */
  const handleDuplicateEntityListView = (
    entitySlug: string,
    sourceSlug: string | null,
    name: string,
  ): string => {
    let createdSlug = '';
    setLocalEntities((prev) =>
      prev.map((e) => {
        if (e.slug !== entitySlug) return e;

        // Rozwiąż źródłowy listConfig: null = domyślny (pusty seed),
        // string = nazwany widok listy z localEntities.
        let sourceListConfig: ListConfig = structuredClone(emptyListConfig);
        if (sourceSlug !== null) {
          const sourceView = e.views.find((v) => v.type === 'list' && v.slug === sourceSlug);
          if (sourceView?.listConfig) {
            sourceListConfig = structuredClone(sourceView.listConfig);
          }
        }

        const slug = uniqueViewSlug(name, e.views, 'list_');
        createdSlug = slug;
        const newView: ModuleTemplateViewDefinition = {
          slug,
          name,
          type: 'list',
          isDefault: false,
          sortOrder: e.views.filter((v) => v.type === 'list').length,
          listConfig: sourceListConfig,
        };
        return { ...e, views: [...e.views, newView] };
      }),
    );
    setViewsDirty(true);
    return createdSlug;
  };

  /**
   * Callback: aktualizuje formConfig konkretnego widoku w lokalnych entities.
   * Wywoływany przez MenuItemFormBuilderModal po zapisie canvasu (zanim PATCH menu).
   */
  const handleUpdateViewFormConfig = (
    entitySlug: string,
    viewSlug: string,
    formConfig: ModuleTemplateFormConfig,
  ): void => {
    setLocalEntities((prev) =>
      prev.map((e) => {
        if (e.slug !== entitySlug) return e;
        return {
          ...e,
          views: e.views.map((v) => (v.slug === viewSlug ? { ...v, formConfig } : v)),
        };
      }),
    );
    setViewsDirty(true);
  };

  /**
   * Callback: aktualizuje listConfig konkretnego widoku `list` w lokalnych entities.
   * Wywoływany przez handleViewBuilderSave gdy node ma listRef.
   */
  const handleUpdateViewListConfig = (
    entitySlug: string,
    viewSlug: string,
    listConfig: ListConfig,
  ): void => {
    setLocalEntities((prev) =>
      prev.map((e) => {
        if (e.slug !== entitySlug) return e;
        return {
          ...e,
          views: e.views.map((v) => (v.slug === viewSlug ? { ...v, listConfig } : v)),
        };
      }),
    );
    setViewsDirty(true);
  };

  // initialRef: poczatkowy stan (do obliczenia isDirty + reset po 409).
  const initialRef = useRef<MenuItem[]>(initialMenu);

  // Reset working copy gdy initialMenu sie zmieni (deep comparison, nie gola referencja).
  // Bez tego rodzic moglby dac nowa tablice co render i triggerowac wieczny reset.
  const prevInitialRef = useRef<MenuItem[]>(initialMenu);
  useEffect(() => {
    if (!menuItemsEqual(initialMenu, prevInitialRef.current)) {
      prevInitialRef.current = initialMenu;
      initialRef.current = initialMenu;
      setHierarchy(initialMenu);
      setSelectedId(null);
    }
  }, [initialMenu]);

  // Paleta jak u klienta (decyzja 2026-10-05): Kartoteki (własne modułu + systemowe + z
  // opublikowanych modułów kreatora, z oznaczeniem modułu), Funkcje (ekrany klienta),
  // Elementy systemowe (tylko Ustawienia).
  const menuSourceQuery = useTemplateMenuSource(templateSlug);
  const source: MenuSourceResponse = useMemo(() => {
    const ownChip = [{ code: templateSlug, name: moduleName ?? templateSlug }];
    // WSZYSTKIE kartoteki szablonu są w palecie — także te z navigationMode='hidden'
    // (np. kartoteki-odwołania jak „Pracownicy”). „hidden” steruje menu AUTOMATYCZNYM
    // (fallback), a paletka służy RĘCZNEMU projektowaniu menu — admin sam decyduje,
    // które kartoteki wpiąć jako pozycje (relacja NIE wyklucza bycia pozycją menu).
    const ownKartoteki: MenuSourceKartoteka[] = localEntities.map((e) => ({
      kind: 'custom',
      slug: `${templateSlug}_${e.slug}`,
      label: e.name,
      iconName: e.icon ?? null,
      usedInModules: ownChip,
    }));
    const remote = menuSourceQuery.data;
    const otherKartoteki = (remote?.kartoteki ?? []).filter(
      (k) => !k.slug.startsWith(`${templateSlug}_`),
    );
    const moduleNames = remote?.moduleNames ?? {};
    const functional: MenuSourceFunctionalItem[] = TENANT_SCREENS.map((screen) => ({
      slug: screen.key,
      label: t(`tenantScreens.${screen.key}`),
      iconName: screen.iconName,
      path: screen.path,
      usedInModules: !screen.owner
        ? []
        : screen.owner.kind === 'module'
          ? [{ code: screen.owner.code, name: moduleNames[screen.owner.code] ?? screen.owner.code }]
          : [{ code: screen.owner.code, name: t('hr.nav.section') }],
    }));
    return {
      // `modules` zostaje dla podglądu menu (etykiety pozycji po kodzie); paleta przy
      // niepustych `kartoteki` sekcji modułów nie pokazuje.
      modules: localEntities.map((e) => ({
        moduleCode: `${templateSlug}_${e.slug}`,
        label: e.name,
        iconName: e.icon ?? null,
        status: 'active' as const,
      })),
      customModules: [],
      systemItems: SYSTEM_ITEMS.filter((item) => item.itemKey === 'settings'),
      installedAreas: [],
      kartoteki: [...ownKartoteki, ...otherKartoteki].sort((a, b) =>
        a.label.localeCompare(b.label, 'pl'),
      ),
      functional,
    };
  }, [localEntities, templateSlug, moduleName, menuSourceQuery.data, t]);

  // Układy kartotek SPOZA modułu (np. Umowy w menu Kadr) — biblioteka kartoteki + wersje
  // robocze zapisywane razem z menu (decyzja 2026-10-05).
  const creatorTargets = menuSourceQuery.data?.creatorKartoteki;
  const foreignViews = useForeignMenuViews(hierarchy, templateSlug, creatorTargets);
  const editorEntities = useMemo(
    () => [...localEntities, ...foreignViews.pseudoEntities],
    [localEntities, foreignViews.pseudoEntities],
  );
  const [foreignModal, setForeignModal] = useState<{
    itemLabel: string;
    moduleCode: string;
    viewType: 'form' | 'list';
    slug: string;
  } | null>(null);

  const hierarchyDirty = useMemo(() => !menuItemsEqual(hierarchy, initialRef.current), [hierarchy]);
  const isDirty = hierarchyDirty || viewsDirty || foreignViews.hasDrafts;

  // Rodzic (edytor szablonu) blokuje „Opublikuj" przy niezapisanym menu — inaczej publikacja
  // po cichu pominęłaby zmiany widoczne na ekranie. Przy odmontowaniu zgłaszamy „czysto".
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // Nazwane layouty do zapisu razem z menu + semantyka „form bez configu jedzie z pustym
  // układem" — logika i testy w `menu-view-upserts.ts` (fix wiszącego viewRef, 2026-07-20).
  const viewUpserts = useMemo<TemplateMenuViewUpsert[]>(
    () => [...buildMenuViewUpserts(localEntities), ...foreignViews.upserts],
    [localEntities, foreignViews.upserts],
  );

  const selectedItem = useMemo<MenuItem | null>(
    () => (selectedId ? findInTree(hierarchy, selectedId) : null),
    [hierarchy, selectedId],
  );

  // Nowo dodana pozycja zostaje ZWINIĘTA (feedback Eugenii 2026-09-30) — bez auto-otwarcia
  // szczegółów liścia. WYJĄTEK: nowa sekcja zostaje zaznaczona (nazwanie + nesting kolejnym
  // „+"); przy liściu NIE ruszamy zaznaczenia (inaczej dodanie liścia odznacza sekcję).
  const selectIfSection = (item: MenuItem) => {
    if (item.type === 'section') setSelectedId(item.id);
  };

  const handleAddItem = (newItem: MenuItem | AdminMenuItem) => {
    const item = newItem as MenuItem;
    if (selectedItem && selectedItem.type === 'section') {
      setHierarchy((h) => insertIntoContainer(h, selectedItem.id, item));
    } else {
      setHierarchy((h) => insertIntoContainer(h, null, item));
    }
    selectIfSection(item);
  };

  const handleDeleteItem = (id: string) => {
    setPendingDeleteId(id);
  };

  const confirmDeleteItem = () => {
    if (!pendingDeleteId) return;
    setHierarchy((h) => removeFromTree(h, pendingDeleteId));
    if (selectedId === pendingDeleteId) setSelectedId(null);
    setPendingDeleteId(null);
  };

  const handleUpdateItem = (next: MenuItem | AdminMenuItem) => {
    const item = next as MenuItem;
    setHierarchy((h) => updateInTree(h, item.id, () => item));
  };

  const handleReorder = (parentId: string | null, fromIndex: number, toIndex: number) => {
    setHierarchy((h) => reorderInContainer(h, parentId, fromIndex, toIndex));
  };

  // Przeniesienie uchwytem między poziomami (główny ↔ sekcja).
  const handleMove = (id: string, containerId: string | null, index: number) => {
    setHierarchy((h) => moveInTree(h, id, containerId, index));
  };

  // Dodanie pozycji z palety przez upuszczenie w konkretne miejsce (drag & drop).
  const handleAddItemAt = (
    containerId: string | null,
    index: number,
    newItem: MenuItem | AdminMenuItem,
  ) => {
    const item = newItem as MenuItem;
    setHierarchy((h) => insertIntoContainer(h, containerId, item, index));
    selectIfSection(item);
  };

  // Tożsamości pozycji już w menu — do znacznika „w menu" w palecie.
  const usedPaletteKeys = useMemo(() => collectUsedPaletteKeys(hierarchy), [hierarchy]);

  /**
   * Etykiety (chip) layoutu formularza i listy dla pozycji menu type='module'.
   * Formularz: nazwa nazwanego layoutu (z viewRef) / „Własny" (inline view.formConfig) /
   * „Domyślny". Lista: „Własny" gdy ustawiony inline view.listConfig, inaczej „Domyślny"
   * (nazwane layouty list to przyszły krok — dziś lista jest inline lub domyślna).
   */
  const resolveViewLabels = useCallback(
    (item: MenuItem): MenuItemViewLabels | null => {
      if (item.type !== 'module' || !item.moduleCode) return null;
      const prefix = `${templateSlug}_`;
      const entitySlug = item.moduleCode.startsWith(prefix)
        ? item.moduleCode.slice(prefix.length)
        : null;
      const entity = entitySlug ? localEntities.find((e) => e.slug === entitySlug) : null;
      if (!entity) return null;

      const defaultName = t('admin.templateMenuBuilder.formBuilder.defaultLayoutName', {
        defaultValue: 'Domyślny',
      });
      const customName = t('admin.menuLayoutBuilder.viewChips.custom', { defaultValue: 'Własny' });

      let formName = defaultName;
      if (item.viewRef) {
        const parsed = parseMenuViewRef(item.viewRef);
        const v = parsed
          ? entity.views.find((vv) => vv.type === 'form' && vv.slug === parsed.viewSlug)
          : undefined;
        formName = v?.name ?? defaultName;
      } else if (item.view?.formConfig) {
        formName = customName;
      }

      let listName = defaultName;
      if (item.listRef) {
        const parsed = parseMenuViewRef(item.listRef);
        const v = parsed
          ? entity.views.find((vv) => vv.type === 'list' && vv.slug === parsed.viewSlug)
          : undefined;
        listName = v?.name ?? defaultName;
      } else if (item.view?.listConfig) {
        listName = customName;
      }

      return {
        form: t('admin.menuLayoutBuilder.viewChips.form', {
          name: formName,
          defaultValue: `Formularz: ${formName}`,
        }),
        list: t('admin.menuLayoutBuilder.viewChips.list', {
          name: listName,
          defaultValue: `Lista: ${listName}`,
        }),
      };
    },
    [templateSlug, localEntities, t],
  );

  /**
   * Rozwiązuje pozycję menu na dane podglądu „na żywo" (zunifikowany modal):
   * encję (kartotekę) + AKTYWNY layout formularza i AKTYWNY widok listy tej pozycji
   * + nazwy nazwanych widoków (chip w nagłówku prawego panelu; null = domyślny).
   * Precedencja jak w runtime: nazwany layout (viewRef/listRef) → inline (view.*)
   * → domyślny widok encji. Kartoteki: `new_table` = pola z encji szablonu;
   * odwołania do kartotek systemowych (temat 3) = pola z definicji kartoteki
   * (fetch wyżej; null dopóki definicja się nie załaduje → notka w prawym panelu).
   * Odwołania cross-szablonowe (`reference_to_creator_entity`) — wciąż bez podglądu.
   */
  const resolveNodePreview = useCallback(
    (node: MenuItem): MenuNodePreviewData | null => {
      if (node.type !== 'module' || !node.moduleCode) return null;
      const prefix = `${templateSlug}_`;
      const entitySlug = node.moduleCode.startsWith(prefix)
        ? node.moduleCode.slice(prefix.length)
        : null;
      if (!entitySlug) return null;
      const entity = localEntities.find((e) => e.slug === entitySlug);
      if (!entity) return null;

      let fields: ModuleTemplateFieldDefinition[];
      if (entity.ddlStrategy === 'new_table') {
        fields = entity.fields;
      } else if (entity.ddlStrategy === 'reference_to_system_entity') {
        const mapped = entity.referenceSystemModuleSlug
          ? kartotekaPreviewFieldsBySlug.get(entity.referenceSystemModuleSlug)
          : undefined;
        if (!mapped || mapped.length === 0) return null;
        fields = mapped;
      } else {
        return null;
      }

      const customName = t('admin.menuLayoutBuilder.viewChips.custom', { defaultValue: 'Własny' });

      let formConfig: ModuleTemplateFormConfig | undefined;
      let formViewName: string | null = null;
      if (node.viewRef) {
        const parsed = parseMenuViewRef(node.viewRef);
        const view = parsed
          ? entity.views.find((v) => v.type === 'form' && v.slug === parsed.viewSlug)
          : undefined;
        formConfig = view?.formConfig;
        formViewName = view?.name ?? null;
      } else if (node.view?.formConfig) {
        formConfig = node.view.formConfig;
        formViewName = customName;
      } else {
        formConfig = entity.views.find((v) => v.type === 'form' && v.isDefault)?.formConfig;
      }

      let listConfig: ListConfig | undefined;
      let listViewName: string | null = null;
      if (node.listRef) {
        const parsed = parseMenuViewRef(node.listRef);
        const view = parsed
          ? entity.views.find((v) => v.type === 'list' && v.slug === parsed.viewSlug)
          : undefined;
        listConfig = view?.listConfig;
        listViewName = view?.name ?? null;
      } else if (node.view?.listConfig) {
        listConfig = node.view.listConfig;
        listViewName = customName;
      } else {
        listConfig = entity.views.find((v) => v.type === 'list' && v.isDefault)?.listConfig;
      }

      return {
        entityName: entity.name,
        entitySlug: entity.slug,
        fields,
        formConfig,
        listConfig,
        formViewName,
        listViewName,
      };
    },
    [localEntities, templateSlug, t, kartotekaPreviewFieldsBySlug],
  );

  const handleSave = async () => {
    // Lustro serwerowego collectViewRefProblems na stanie lokalnym — user dostaje
    // KONKRETNĄ pozycję do naprawy zamiast generycznego 422 z serwera.
    const dangling = collectDanglingMenuRefLabels(
      hierarchy,
      editorEntities,
      foreignViews.unloadedCodes,
    );
    if (dangling.length > 0) {
      toast.show({
        variant: 'error',
        title: t('admin.templateMenuBuilder.errors.danglingViewRef', {
          items: dangling.join(', '),
        }),
      });
      return;
    }
    setSaving(true);
    try {
      // Menu robocze → module_menus (bez wersji); wybrane → modules.default_menu (+ wersja).
      const saved = draftMenuId
        ? (await patchDraftMenu(templateSlug, draftMenuId, { hierarchy, views: viewUpserts }))
            .hierarchy
        : (await patchTemplateMenu(templateSlug, hierarchy, viewUpserts)).hierarchy;
      initialRef.current = saved;
      setHierarchy(saved);
      setViewsDirty(false);
      foreignViews.commitDrafts();
      queryClient.invalidateQueries({ queryKey: moduleTemplateDetailQueryKey(templateSlug) });
      toast.show({
        variant: 'success',
        title: draftMenuId
          ? t('admin.templateMenuBuilder.toasts.savedDraft')
          : t('admin.templateMenuBuilder.toasts.saved'),
      });
    } catch (err) {
      const code = extractTemplateApiErrorCode(err);
      if (code === 'VERSION_CONFLICT') {
        toast.show({
          variant: 'warning',
          title: t('admin.templateMenuBuilder.errors.versionConflict'),
        });
        // Po konflikcie resetuj working copy — rodzic (ModuleTemplateEditor) dostanie
        // swiezy defaultMenu po invalidacji i przekaze przez initialMenu.
        queryClient.invalidateQueries({ queryKey: moduleTemplateDetailQueryKey(templateSlug) });
      } else if (code === 'UNKNOWN_MENU_TARGET') {
        // Pozycja wskazuje kartotekę, której klient nie ma (np. moduł wycofany z publikacji).
        toast.show({
          variant: 'error',
          title: t('admin.templateMenuBuilder.errors.unknownTarget'),
        });
      } else {
        toast.show({ variant: 'error', title: t('admin.templateMenuBuilder.errors.saveFailed') });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleViewBuilderSave = (listConfig: ListConfig) => {
    if (!viewBuilderNode) return;

    // Gdy node ma listRef → zapisz listConfig do nazwanego widoku w localEntities
    // i upewnij się że node ma listRef a nie inline view.listConfig.
    if (viewBuilderListSlug !== undefined && viewBuilderListSlug !== null) {
      const moduleCode = viewBuilderNode.moduleCode ?? '';
      const prefix = `${templateSlug}_`;
      const entitySlug = moduleCode.startsWith(prefix) ? moduleCode.slice(prefix.length) : null;
      if (entitySlug) {
        handleUpdateViewListConfig(entitySlug, viewBuilderListSlug, listConfig);
        const listRef = `${entitySlug}:${viewBuilderListSlug}`;
        setHierarchy((h) =>
          updateInTree(h, viewBuilderNode.id, (n) => {
            // Usuń inline listConfig (jeśli był), ustaw listRef
            const { view, ...rest } = n;
            const nextView = view
              ? { formConfig: view.formConfig, baseFilters: view.baseFilters, v: 1 as const }
              : undefined;
            return {
              ...rest,
              ...(nextView && Object.keys(nextView).filter((k) => k !== 'v').length > 0
                ? { view: nextView }
                : {}),
              listRef,
            };
          }),
        );
      }
    } else {
      // Stara ścieżka / domyślny (inline) widok listy
      setHierarchy((h) =>
        updateInTree(h, viewBuilderNode.id, (n) => ({
          ...n,
          view: { ...n.view, listConfig, v: 1 as const },
        })),
      );
    }
    setViewBuilderNode(null);
    setViewBuilderListSlug(undefined);
  };

  /**
   * Zapis formularza z buildera.
   *
   * Gdy node ma viewRef ('entitySlug:viewSlug') — aktualizujemy formConfig
   * w lokalnych entities (handleUpdateViewFormConfig). Node.view zostaje bez zmian
   * (viewRef wskazuje na entity.views).
   *
   * Gdy brak viewRef (stary inline) — zapisujemy do node.view.formConfig (fallback
   * dla istniejących szablonów, zero regresji).
   */
  const handleFormBuilderSave = (formConfig: ModuleTemplateFormConfig, viewRef?: string) => {
    if (!formBuilderNode) return;
    if (viewRef) {
      // Nowa ścieżka: viewRef = 'entitySlug:viewSlug'
      const [entitySlug, viewSlug] = viewRef.split(':');
      if (entitySlug && viewSlug) {
        handleUpdateViewFormConfig(entitySlug, viewSlug, formConfig);
        // Upewnij się że node ma viewRef i nie ma inline view.formConfig
        setHierarchy((h) =>
          updateInTree(h, formBuilderNode.id, (n) => {
            // Usuń inline formConfig (jeśli był), ustaw viewRef
            const { view, ...rest } = n;
            const nextView = view
              ? { listConfig: view.listConfig, baseFilters: view.baseFilters, v: 1 as const }
              : undefined;
            return {
              ...rest,
              ...(nextView && Object.keys(nextView).length > 1 ? { view: nextView } : {}),
              viewRef,
            };
          }),
        );
      }
    } else {
      // Stara ścieżka: inline embed (istniejące szablony)
      setHierarchy((h) =>
        updateInTree(h, formBuilderNode.id, (n) => ({
          ...n,
          view: { ...n.view, formConfig, v: 1 as const },
        })),
      );
    }
    setFormBuilderNode(null);
    setFormBuilderInitialSlug(undefined);
  };

  // ── Przypisanie widoków (inline w akordeonie) ─────────────────────────────

  /** Przypisz layout formularza do pozycji: viewRef ('entitySlug:viewSlug') lub null=domyślny. */
  const handleAssignForm = (node: MenuItem, viewRef: string | null) => {
    setHierarchy((h) =>
      updateInTree(h, node.id, (n) => {
        if (viewRef) {
          // Nazwany layout: ustaw viewRef, usuń inline view.formConfig (zachowaj listConfig).
          const { view, ...rest } = n;
          const nextView = view
            ? { listConfig: view.listConfig, baseFilters: view.baseFilters, v: 1 as const }
            : undefined;
          return {
            ...rest,
            ...(nextView && Object.keys(nextView).length > 1 ? { view: nextView } : {}),
            viewRef,
          };
        }
        // Domyślny: usuń viewRef (inline view.formConfig — jeśli był — zostaje jako custom default).
        const { viewRef: _vr, ...rest } = n;
        return rest;
      }),
    );
  };

  /** „Przeglądaj/edytuj" formularz z akordeonu — otwiera builder dla danego layoutu (edytowalny). */
  const handleEditFormFromAssignment = (node: MenuItem, viewSlug: string | null) => {
    setFormBuilderInitialSlug(viewSlug);
    setFormBuilderNode(node);
    setBuilderReadOnly(false);
  };

  /** Przypisz nazwany layout listy do pozycji: listRef ('entitySlug:viewSlug') lub null=domyślny. */
  const handleAssignList = (node: MenuItem, listRef: string | null) => {
    setHierarchy((h) =>
      updateInTree(h, node.id, (n) => {
        if (listRef) {
          // Nazwany layout: ustaw listRef, usuń inline view.listConfig (zachowaj formConfig).
          const { view, ...rest } = n;
          const nextView = view
            ? { formConfig: view.formConfig, baseFilters: view.baseFilters, v: 1 as const }
            : undefined;
          return {
            ...rest,
            ...(nextView && Object.keys(nextView).filter((k) => k !== 'v').length > 0
              ? { view: nextView }
              : {}),
            listRef,
          };
        }
        // Domyślny: usuń listRef
        const { listRef: _lr, ...rest } = n;
        return rest;
      }),
    );
  };

  /** „Przeglądaj/edytuj" listę z akordeonu — otwiera builder listy (edytowalny). */
  const handleEditListFromAssignment = (node: MenuItem, viewSlug: string | null) => {
    setViewBuilderListSlug(viewSlug);
    setViewBuilderNode(node);
    setBuilderReadOnly(false);
  };

  /** „+ Nowy widok listy" z akordeonu — tworzy nazwany layout (pusty seed). */
  const handleCreateListFromAssignment = (node: MenuItem, name: string): string => {
    const prefix = `${templateSlug}_`;
    const entitySlug = node.moduleCode?.startsWith(prefix)
      ? node.moduleCode.slice(prefix.length)
      : null;
    if (!entitySlug) return '';
    return handleCreateEntityListView(entitySlug, name);
  };

  /** „+ Nowy formularz" z akordeonu — tworzy nazwany layout (pusty seed). */
  const handleCreateFormFromAssignment = (node: MenuItem, name: string): string => {
    const prefix = `${templateSlug}_`;
    const entitySlug = node.moduleCode?.startsWith(prefix)
      ? node.moduleCode.slice(prefix.length)
      : null;
    if (!entitySlug) return '';
    // Pusty seed → builder zasieje pełny domyślny układ przy otwarciu (dual-read).
    return handleCreateEntityView(entitySlug, name, { elements: [] });
  };

  /** „Duplikuj" z akordeonu — tworzy kopię nazwanego lub domyślnego layoutu formularza. */
  const handleDuplicateFormFromAssignment = (
    node: MenuItem,
    sourceSlug: string | null,
    name: string,
  ): string => {
    const prefix = `${templateSlug}_`;
    const entitySlug = node.moduleCode?.startsWith(prefix)
      ? node.moduleCode.slice(prefix.length)
      : null;
    if (!entitySlug) return '';
    return handleDuplicateEntityView(entitySlug, sourceSlug, name);
  };

  /** „Duplikuj" z akordeonu — tworzy kopię nazwanego lub domyślnego layoutu listy. */
  const handleDuplicateListFromAssignment = (
    node: MenuItem,
    sourceSlug: string | null,
    name: string,
  ): string => {
    const prefix = `${templateSlug}_`;
    const entitySlug = node.moduleCode?.startsWith(prefix)
      ? node.moduleCode.slice(prefix.length)
      : null;
    if (!entitySlug) return '';
    return handleDuplicateEntityListView(entitySlug, sourceSlug, name);
  };

  /**
   * „Usuń" nazwany layout formularza z akordeonu.
   * Usuwa widok z localEntities; jeśli był aktywny dla pozycji (viewRef) — resetuje
   * przypisanie pozycji do domyślnego (czyści viewRef → radio wraca na „Domyślny").
   */
  const handleDeleteFormFromAssignment = (node: MenuItem, viewSlug: string) => {
    const prefix = `${templateSlug}_`;
    const entitySlug = node.moduleCode?.startsWith(prefix)
      ? node.moduleCode.slice(prefix.length)
      : null;
    if (!entitySlug) return;
    setLocalEntities((prev) =>
      prev.map((e) =>
        e.slug === entitySlug
          ? { ...e, views: e.views.filter((v) => !(v.type === 'form' && v.slug === viewSlug)) }
          : e,
      ),
    );
    setViewsDirty(true);
    // Edge case: usuwany layout był aktywny dla pozycji → reset do domyślnego.
    if (node.viewRef) {
      const parsed = parseMenuViewRef(node.viewRef);
      if (parsed?.entitySlug === entitySlug && parsed.viewSlug === viewSlug) {
        handleAssignForm(node, null);
      }
    }
  };

  /**
   * „Usuń" nazwany layout listy z akordeonu. Analogicznie do formularza
   * (czyści listRef gdy usuwany layout był aktywny dla pozycji).
   */
  const handleDeleteListFromAssignment = (node: MenuItem, viewSlug: string) => {
    const prefix = `${templateSlug}_`;
    const entitySlug = node.moduleCode?.startsWith(prefix)
      ? node.moduleCode.slice(prefix.length)
      : null;
    if (!entitySlug) return;
    setLocalEntities((prev) =>
      prev.map((e) =>
        e.slug === entitySlug
          ? { ...e, views: e.views.filter((v) => !(v.type === 'list' && v.slug === viewSlug)) }
          : e,
      ),
    );
    setViewsDirty(true);
    if (node.listRef) {
      const parsed = parseMenuViewRef(node.listRef);
      if (parsed?.entitySlug === entitySlug && parsed.viewSlug === viewSlug) {
        handleAssignList(node, null);
      }
    }
  };

  /**
   * Inline rename (ołówek na wierszu) nazwanego layoutu formularza z akordeonu —
   * zmienia tylko `name` widoku w localEntities (slug niezmienny), zapis przy „Zapisz
   * strukturę" (PUT). Analogicznie do „Duplikuj"/„Usuń" wyżej — spójność z tenantowym
   * builderem menu (MenuBuilderShell.onRenameForm).
   */
  const handleRenameFormFromAssignment = (node: MenuItem, viewSlug: string, name: string) => {
    const prefix = `${templateSlug}_`;
    const entitySlug = node.moduleCode?.startsWith(prefix)
      ? node.moduleCode.slice(prefix.length)
      : null;
    if (!entitySlug) return;
    setLocalEntities((prev) =>
      prev.map((e) =>
        e.slug === entitySlug
          ? {
              ...e,
              views: e.views.map((v) =>
                v.type === 'form' && v.slug === viewSlug ? { ...v, name } : v,
              ),
            }
          : e,
      ),
    );
    setViewsDirty(true);
  };

  /** Inline rename nazwanego widoku listy — analogicznie do formularza (`type === 'list'`). */
  const handleRenameListFromAssignment = (node: MenuItem, viewSlug: string, name: string) => {
    const prefix = `${templateSlug}_`;
    const entitySlug = node.moduleCode?.startsWith(prefix)
      ? node.moduleCode.slice(prefix.length)
      : null;
    if (!entitySlug) return;
    setLocalEntities((prev) =>
      prev.map((e) =>
        e.slug === entitySlug
          ? {
              ...e,
              views: e.views.map((v) =>
                v.type === 'list' && v.slug === viewSlug ? { ...v, name } : v,
              ),
            }
          : e,
      ),
    );
    setViewsDirty(true);
  };

  /** „Podgląd" z akordeonu — otwiera builder formularza w trybie read-only. */
  const handlePreviewFormFromAssignment = (node: MenuItem, viewSlug: string | null) => {
    setFormBuilderInitialSlug(viewSlug);
    setFormBuilderNode(node);
    setBuilderReadOnly(true);
  };

  /** „Podgląd" z akordeonu — otwiera builder listy w trybie read-only. */
  const handlePreviewListFromAssignment = (node: MenuItem, viewSlug: string | null) => {
    setViewBuilderListSlug(viewSlug);
    setViewBuilderNode(node);
    setBuilderReadOnly(true);
  };

  const foreignCode = foreignLayoutsCode(selectedItem, templateSlug, creatorTargets);

  /** Akcje akordeonu „Layout formularza / Widok listy" dla pozycji obcej kartoteki. */
  const foreignEditorHandlers = (node: MenuItem, code: string) => {
    const label = node.label ?? code;
    const open = (viewType: 'form' | 'list', slug: string) =>
      setForeignModal({ itemLabel: label, moduleCode: code, viewType, slug });
    const create = (
      viewType: 'form' | 'list',
      name: string,
      sourceSlug: string | null | undefined,
    ) => {
      // Slug nowego układu liczony z biblioteki — bez niej mógłby trafić w istniejący układ
      // i nadpisać go przy zapisie. Czekamy, aż się wczyta.
      if (foreignViews.failedCodes.has(code)) {
        // Wczytanie padło — ponawiamy w tle i mówimy wprost, co się stało.
        foreignViews.retryLibrary(code);
        toast.show({ variant: 'error', title: t('admin.menuItemViews.foreign.libraryFailed') });
        return undefined;
      }
      if (foreignViews.unloadedCodes.has(code)) {
        toast.show({ variant: 'info', title: t('admin.menuItemViews.foreign.libraryLoading') });
        return undefined;
      }
      const source = sourceSlug ? foreignViews.findView(code, viewType, sourceSlug) : null;
      const config = source
        ? ((viewType === 'form' ? source.formConfig : source.listConfig) ?? null)
        : null;
      const slug = foreignViews.createDraft(code, viewType, name, config);
      const ref = `${code}:${slug}`;
      if (viewType === 'form') handleAssignForm(node, ref);
      else handleAssignList(node, ref);
      open(viewType, slug);
      return slug;
    };
    const remove = (viewType: 'form' | 'list', slug: string) => {
      const result = foreignViews.removeDraft(code, viewType, slug);
      if (result === 'library') {
        // Układ z biblioteki (mógł już trafić do klientów) — usuwa się na ekranie Kartoteki.
        toast.show({ variant: 'info', title: t('admin.menuItemViews.foreign.deleteInLibrary') });
        return;
      }
      if (result === 'reverted') {
        // Niezapisana zmiana układu z biblioteki cofnięta — układ zostaje.
        toast.show({ variant: 'info', title: t('admin.menuItemViews.foreign.changeReverted') });
        return;
      }
      const ref = `${code}:${slug}`;
      if (viewType === 'form' && node.viewRef === ref) handleAssignForm(node, null);
      if (viewType === 'list' && node.listRef === ref) handleAssignList(node, null);
    };
    return {
      onDesignForm: (slug: string | null) => (slug ? open('form', slug) : undefined),
      onDesignList: (slug: string | null) => (slug ? open('list', slug) : undefined),
      onPreviewForm: undefined,
      onPreviewList: undefined,
      onLivePreviewForm: undefined,
      onLivePreviewList: undefined,
      onCreateForm: (name: string) => create('form', name, null),
      onCreateList: (name: string) => create('list', name, null),
      onDuplicateForm: (sourceSlug: string | null, name: string) =>
        create('form', name, sourceSlug),
      onDuplicateList: (sourceSlug: string | null, name: string) =>
        create('list', name, sourceSlug),
      onDeleteForm: (slug: string) => remove('form', slug),
      onDeleteList: (slug: string) => remove('list', slug),
      onRenameForm: (slug: string, name: string) =>
        foreignViews.updateView(code, 'form', slug, { name }),
      onRenameList: (slug: string, name: string) =>
        foreignViews.updateView(code, 'list', slug, { name }),
    };
  };

  return (
    // Jeden root (NIE fragment) — pasek akcji + kreator jako kolumna o ograniczonej wysokości.
    // min-w-0 pozwala zwęzić się w kolumnie flex-1 edytora bez wypychania układu.
    <div className={`flex min-w-0 flex-col ${heightClassName}`}>
      {/* Pasek akcji — podgląd menu tenanta zawsze dostępny (read-only-safe);
          znacznik zmian + Zapisz tylko gdy builder edytowalny. */}
      <div className="mb-3 flex shrink-0 items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            // Wejście z paska: bez pozycji startowej — modal zaznaczy pierwszą kartotekę.
            setPreviewInitialNodeId(null);
            setPreviewInitialMode('list');
            setMenuPreviewOpen(true);
          }}
          className="flex items-center gap-1.5 rounded-md border border-border-secondary px-3 py-1.5 text-sm font-medium text-text-secondary transition hover:bg-bg-tertiary hover:text-text-primary"
          data-testid="template-menu-builder-preview"
        >
          <Eye className="h-4 w-4" />
          {t('admin.menuLayoutBuilder.preview.button')}
        </button>
        {!readOnly && (
          <>
            {isDirty && (
              <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                {t('admin.templateMenuBuilder.dirty')}
              </span>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="flex items-center gap-1.5 rounded-md bg-accent-500 px-3 py-1.5 text-sm font-medium text-white transition disabled:opacity-50 enabled:hover:bg-accent-600"
              data-testid="template-menu-builder-save"
            >
              <Save className="h-4 w-4" />
              {saving ? t('admin.templateMenuBuilder.saving') : t('admin.templateMenuBuilder.save')}
            </button>
          </>
        )}
      </div>

      {/* Canvas + paletka — kolumna właściwości usunięta; akordeon inline pod wierszem */}
      <div
        className="flex min-h-0 flex-1 overflow-hidden rounded-lg border border-border-secondary"
        data-testid="menu-builder-embedded"
      >
        {paletteOpen ? (
          <>
            <MenuPalette
              source={source}
              isLoading={false}
              onAdd={readOnly ? () => undefined : handleAddItem}
              i18nPrefix={SUB_PREFIX}
              allowModuleType
              widthPx={paletteWidth}
              onCollapse={() => setPaletteOpen(false)}
              enableDrag={!readOnly}
              usedKeys={usedPaletteKeys}
              collapsedStorageScope={PALETTE_COLLAPSED_SCOPE}
              loadError={
                menuSourceQuery.isError
                  ? (menuSourceQuery.error as { response?: { status?: number } } | null)?.response
                      ?.status === 403
                    ? 'forbidden'
                    : 'failed'
                  : null
              }
            />
            <ColumnResizer
              width={paletteWidth}
              onWidthChange={setPaletteWidth}
              min={200}
              max={560}
            />
          </>
        ) : (
          <CollapsedColumnRail
            label={t(`${SUB_PREFIX}.palette.header`, { defaultValue: 'Paleta' })}
            ariaLabel={t(`${SUB_PREFIX}.showPalette`, { defaultValue: 'Pokaż paletę' })}
            onExpand={() => setPaletteOpen(true)}
            testId="menu-palette-expand"
          />
        )}
        <MenuCanvas
          hierarchy={hierarchy}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onDeselect={() => setSelectedId(null)}
          onDelete={readOnly ? () => undefined : handleDeleteItem}
          onReorder={readOnly ? () => undefined : handleReorder}
          onMove={readOnly ? undefined : handleMove}
          enableDrag={!readOnly}
          onAddAt={readOnly ? undefined : handleAddItemAt}
          i18nPrefix={SUB_PREFIX}
          resolveViewLabels={resolveViewLabels}
          onChangeItem={readOnly ? undefined : handleUpdateItem}
          inlineEditorProps={
            readOnly
              ? undefined
              : {
                  templateCode: templateSlug,
                  entities: editorEntities,
                  // Własne kartoteki: slug encji = moduleCode bez prefiksu szablonu; obce
                  // kartoteki systemowe: slug „encji" = kod kartoteki (patrz use-foreign-menu-views).
                  resolveEntitySlug: (item) => {
                    const code = (item as MenuItem).moduleCode;
                    if (item.type !== 'module' || !code) return null;
                    if (code.startsWith(`${templateSlug}_`)) {
                      return code.slice(templateSlug.length + 1);
                    }
                    return foreignLayoutsCode(item as MenuItem, templateSlug, creatorTargets);
                  },
                  onAssignForm: selectedItem
                    ? (viewRef) => handleAssignForm(selectedItem as MenuItem, viewRef)
                    : undefined,
                  onDesignForm: selectedItem
                    ? (viewSlug) => handleEditFormFromAssignment(selectedItem as MenuItem, viewSlug)
                    : undefined,
                  onDesignList: selectedItem
                    ? (viewSlug) => handleEditListFromAssignment(selectedItem as MenuItem, viewSlug)
                    : undefined,
                  onCreateForm: selectedItem
                    ? (name) => handleCreateFormFromAssignment(selectedItem as MenuItem, name)
                    : undefined,
                  onDuplicateForm: selectedItem
                    ? (sourceSlug, name) =>
                        handleDuplicateFormFromAssignment(
                          selectedItem as MenuItem,
                          sourceSlug,
                          name,
                        )
                    : undefined,
                  onAssignList: selectedItem
                    ? (listRef) => handleAssignList(selectedItem as MenuItem, listRef)
                    : undefined,
                  onCreateList: selectedItem
                    ? (name) => handleCreateListFromAssignment(selectedItem as MenuItem, name)
                    : undefined,
                  onDuplicateList: selectedItem
                    ? (sourceSlug, name) =>
                        handleDuplicateListFromAssignment(
                          selectedItem as MenuItem,
                          sourceSlug,
                          name,
                        )
                    : undefined,
                  onLivePreviewForm: selectedItem
                    ? () => {
                        setPreviewInitialMode('form');
                        setPreviewInitialNodeId((selectedItem as MenuItem).id);
                        setMenuPreviewOpen(true);
                      }
                    : undefined,
                  onLivePreviewList: selectedItem
                    ? () => {
                        setPreviewInitialMode('list');
                        setPreviewInitialNodeId((selectedItem as MenuItem).id);
                        setMenuPreviewOpen(true);
                      }
                    : undefined,
                  onPreviewForm: selectedItem
                    ? (viewSlug) =>
                        handlePreviewFormFromAssignment(selectedItem as MenuItem, viewSlug)
                    : undefined,
                  onPreviewList: selectedItem
                    ? (viewSlug) =>
                        handlePreviewListFromAssignment(selectedItem as MenuItem, viewSlug)
                    : undefined,
                  onDeleteForm: selectedItem
                    ? (viewSlug) =>
                        handleDeleteFormFromAssignment(selectedItem as MenuItem, viewSlug)
                    : undefined,
                  onDeleteList: selectedItem
                    ? (viewSlug) =>
                        handleDeleteListFromAssignment(selectedItem as MenuItem, viewSlug)
                    : undefined,
                  onRenameForm: selectedItem
                    ? (viewSlug, name) =>
                        handleRenameFormFromAssignment(selectedItem as MenuItem, viewSlug, name)
                    : undefined,
                  onRenameList: selectedItem
                    ? (viewSlug, name) =>
                        handleRenameListFromAssignment(selectedItem as MenuItem, viewSlug, name)
                    : undefined,
                  // Pozycja obcej kartoteki — te same akcje, ale na bibliotece kartoteki
                  // (wersje robocze do „Zapisz menu"); nadpisują handlery kartotek modułu.
                  ...(foreignCode && selectedItem
                    ? foreignEditorHandlers(selectedItem as MenuItem, foreignCode)
                    : {}),
                }
          }
        />
      </div>

      {foreignModal
        ? (() => {
            const view = foreignViews.findView(
              foreignModal.moduleCode,
              foreignModal.viewType,
              foreignModal.slug,
            );
            if (!view) return null;
            return (
              <ForeignKartotekaViewModal
                key={`${foreignModal.moduleCode}:${foreignModal.viewType}:${foreignModal.slug}`}
                moduleCode={foreignModal.moduleCode}
                identity={foreignViews.identityOf(foreignModal.moduleCode)}
                creatorInfo={creatorTargets?.[foreignModal.moduleCode]}
                itemLabel={foreignModal.itemLabel}
                view={{
                  slug: view.slug,
                  name: view.name,
                  viewType: foreignModal.viewType,
                  config:
                    (foreignModal.viewType === 'form' ? view.formConfig : view.listConfig) ?? null,
                  sortOrder: view.sortOrder,
                }}
                onSave={(config) =>
                  foreignViews.updateView(
                    foreignModal.moduleCode,
                    foreignModal.viewType,
                    foreignModal.slug,
                    { config },
                  )
                }
                onClose={() => setForeignModal(null)}
              />
            );
          })()
        : null}

      <MenuUnifiedPreview
        open={menuPreviewOpen}
        onClose={() => setMenuPreviewOpen(false)}
        hierarchy={hierarchy}
        modules={source.modules}
        moduleName={moduleName}
        initialNodeId={previewInitialNodeId}
        initialMode={previewInitialMode}
        resolveNode={resolveNodePreview}
      />

      {viewBuilderNode ? (
        <MenuItemViewBuilderModal
          node={viewBuilderNode}
          templateCode={templateSlug}
          entities={localEntities}
          namedViewSlug={viewBuilderListSlug ?? null}
          onSave={handleViewBuilderSave}
          onClose={() => {
            setViewBuilderNode(null);
            setViewBuilderListSlug(undefined);
            setBuilderReadOnly(false);
          }}
          readOnlyMode={builderReadOnly}
        />
      ) : null}

      {formBuilderNode ? (
        <MenuItemFormBuilderModal
          node={formBuilderNode}
          templateCode={templateSlug}
          entities={localEntities}
          onSave={handleFormBuilderSave}
          onClose={() => {
            setFormBuilderNode(null);
            setFormBuilderInitialSlug(undefined);
            setBuilderReadOnly(false);
          }}
          onCreateView={handleCreateEntityView}
          initialViewSlugOverride={formBuilderInitialSlug}
          readOnlyMode={builderReadOnly}
        />
      ) : null}

      {(() => {
        const pendingItem = pendingDeleteId ? findInTree(hierarchy, pendingDeleteId) : null;
        const label = pendingItem?.label?.trim() || t(`${SUB_PREFIX}.deleteItem.fallbackLabel`);
        return (
          <ConfirmDialog
            open={pendingDeleteId !== null}
            variant="danger"
            title={t(`${SUB_PREFIX}.deleteItem.title`)}
            message={t(`${SUB_PREFIX}.deleteItem.message`, { label })}
            confirmLabel={t('common.delete')}
            onConfirm={confirmDeleteItem}
            onClose={() => setPendingDeleteId(null)}
          />
        );
      })()}
    </div>
  );
}
